import {
	FLAG_ENABLE_TIMERS,
	INCOME_BOOST_COEFFICIENT,
} from "@/config/constants";
import { eventRepository } from "@/event";
import GameError, { GameErrorType } from "@/libs/GameError";
import useQuestStore from "@/store/useQuestStore";
import useResourcesStorageStore from "@/store/useResourcesStorageStore";
import useResourcesStore, {
	UNCAPPED_RESOURCE_TYPES,
} from "@/store/useResourcesStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import useUserStore from "@/store/useUserStore";
import { type QuestActive } from "@/types/quest";
import { type TileCoordinates, type TileIndex } from "@/types/tile";
import { clone } from "@/utils/clone";
import { ceilNumber, floorNumber } from "@/utils/number";
import type TileRepository from "./TileRepository";
import { RoyalProvisionRewardKey, royalProvisionRewards } from "./config/shop";
import type TileEntity from "./entities/TileEntity";
import {
	FrameName,
	tilerepoPoiFrameNames,
	tilerepoPOIs1FrameNames,
	tilerepoSettlementCastleBuildingsFrameNames,
} from "./entities/config";
import { QuestObjectiveActions } from "./entities/config/quests/quest_objective_actions";
import { ResourceType } from "./entities/config/resources";
import AbstractPoiTileEntity from "./entities/poi/AbstractPoiTileEntity";

export type SegmentId = number;

enum AccumulatorName {
	STORAGE_ACCUMULATOR = "storageAccumulator",
	RESOURCE_ACCUMULATOR = "resourceAccumulator",
	INCOME_ACCUMULATOR = "incomeAccumulator",
	UNCAPPED_INCOME_ACCUMULATOR = "uncappedIncomeAccumulator",
	CONSUMPTION_ACCUMULATOR = "consumptionAccumulator",
}

type AccumulatorValue = Partial<Record<ResourceType, number>>;

type SegmentValue = Record<AccumulatorName, AccumulatorValue> & {
	readonly minX: number;
	readonly minY: number;
	readonly maxX: number;
	readonly maxY: number;
	isDirty: boolean;
};

const INCOME_DIFF_RESOURCE_TYPE = new Set<ResourceType>([
	ResourceType.FOOD_INCOME,
	ResourceType.POPULATION_INCOME,
]);

const CONSUMPTION_DIFF_RESOURCE_TYPE = new Set<ResourceType>([
	ResourceType.FOOD_CONSUMPTION,
	ResourceType.POPULATION_CONSUMPTION,
]);

const RESOURCE_INCOME_REGEXP = /Income$/;

const RESOURCE_CONSUMPTION_REGEXP = /Consumption$/;

class SegmentManager {
	/**
	 * Size of one segment in tiles.
	 */
	public readonly SEGMENT_SIZE = 16;

	/**
	 * Map of all segments in the game.
	 */
	public readonly segments = new Map<SegmentId, SegmentValue>();

	/**
	 * Core store.
	 */
	private readonly runtimeStore = useRuntimeStore();

	/**
	 * Resources store.
	 */
	private readonly resourcesStore = useResourcesStore();

	/**
	 * Resources storage store.
	 */
	private readonly resourcesStorageStore = useResourcesStorageStore();

	/**
	 * Quests store.
	 */
	private readonly questStore = useQuestStore();

	/**
	 * Settlements store.
	 */
	// private readonly settlementStore = useSettlementsStore();

	/**
	 * Food penalty coefficient.
	 */
	private FOOD_PENALTY_COEFFICIENT = 1;

	/**
	 * Population penalty coefficient.
	 */
	private POPULATION_PENALTY_COEFFICIENT = 1;

	/**
	 * Returns the number of segments along one side of the map.
	 */
	public get SIZE(): number {
		return this.tilerepo.MAP_SIZE / this.SEGMENT_SIZE;
	}

	/**
	 * Calculates the penalty coefficient based on income and consumption values.
	 */
	public static getPenaltyCoefficient(
		income: number,
		consumption: number,
	): number {
		return consumption > income
			? Math.max(0.9 - (9 * (consumption - income)) / consumption, 0)
			: 1;
	}

	// public getPenaltyCoefficient = (this.constructor as typeof SegmentManager)
	// 	.getPenaltyCoefficient;

	/**
	 * Creates an instance of SegmentManager.
	 */
	public constructor(private readonly tilerepo: TileRepository) {
		this.init();
		this.run(true);

		// eslint-disable-next-line no-console
		console.log(this);
	}

	/**
	 * Initializes all segments in the map.
	 */
	private init(): void {
		const { SIZE } = this;

		for (let segmentId = 0; segmentId < SIZE ** 2; segmentId++) {
			const segmentData = {
				minX: (segmentId % SIZE) * this.SEGMENT_SIZE,
				minY: Math.trunc(segmentId / SIZE) * this.SEGMENT_SIZE,
				maxX: ((segmentId % SIZE) + 1) * this.SEGMENT_SIZE - 1,
				maxY:
					(Math.trunc(segmentId / SIZE) + 1) * this.SEGMENT_SIZE - 1,
				isDirty: true,
				[AccumulatorName.STORAGE_ACCUMULATOR]: {},
				[AccumulatorName.RESOURCE_ACCUMULATOR]: {},
				[AccumulatorName.INCOME_ACCUMULATOR]: {},
				[AccumulatorName.UNCAPPED_INCOME_ACCUMULATOR]: {},
				[AccumulatorName.CONSUMPTION_ACCUMULATOR]: {},
			};

			// mark as read-only
			Object.defineProperties(segmentData, {
				minX: { writable: false },
				minY: { writable: false },
				maxX: { writable: false },
				maxY: { writable: false },
			});

			this.segments.set(segmentId, segmentData);
		}
	}

	/**
	 * Returns whether any of segments is dirty.
	 */
	// private get isDirty(): boolean {
	// 	const values = this.segments.values();
	// 	for (const segmentData of values) {
	// 		if (!segmentData.isDirty) continue;
	// 		return true;
	// 	}
	// 	return false;
	// }

	/**
	 * Returns all dirty segment IDs.
	 */
	// public get dirtySegmentIds(): Set<SegmentId> {
	// 	const segmentIds = new Set<SegmentId>();
	// 	for (const [segmentId, segmentData] of this.segments) {
	// 		if (!segmentData.isDirty) continue;
	// 		segmentIds.add(segmentId);
	// 	}
	// 	return segmentIds;
	// }

	/**
	 * Returns the segmentId for the given tile coordinates.
	 */
	public getSegmentIdByCoordinates({ x, y }: TileCoordinates): SegmentId {
		const { SEGMENT_SIZE, SIZE } = this;
		return (
			Math.trunc(x / SEGMENT_SIZE) + Math.trunc(y / SEGMENT_SIZE) * SIZE
		);
	}

	/**
	 * Returns the segmentId for the given tile index.
	 */
	public getSegmentIdByTileIndex(tileIndex: TileIndex): SegmentId {
		const {
			tilerepo: { MAP_SIZE },
		} = this;
		const x = tileIndex % MAP_SIZE;
		const y = Math.trunc(tileIndex / MAP_SIZE);
		return this.getSegmentIdByCoordinates({ x, y });
	}

	/**
	 * Marks the segment containing the given tile coordinates as dirty.
	 */
	public markSegmentAsDirtyByCoordinates({ x, y }: TileCoordinates): void {
		const segmentId = this.getSegmentIdByCoordinates({ x, y });
		const segmentData = this.segments.get(segmentId);
		if (segmentData && !segmentData.isDirty) segmentData.isDirty = true;

		// console.log("Marking segment as dirty:", { segmentId, x, y });
	}

	/**
	 * Marks the segment containing the given tile index as dirty.
	 */
	public markSegmentAsDirtyByTileIndex(tileIndex: TileIndex): void {
		const segmentId = this.getSegmentIdByTileIndex(tileIndex);
		const segmentData = this.segments.get(segmentId);
		if (segmentData && !segmentData.isDirty) segmentData.isDirty = true;

		// console.log("Marking segment as dirty:", { segmentId, tileIndex });
	}

	/**
	 * Marks all segments as dirty.
	 */
	public markAllSegmentsAsDirty(): void {
		const values = this.segments.values();
		for (const segmentData of values) segmentData.isDirty = true;
	}

	/**
	 * Collects production entities for all dirty segments with one index scan.
	 */
	private getProductionEntitiesBySegmentIds(
		segmentIds: ReadonlySet<SegmentId>,
	): Map<SegmentId, Set<TileEntity>> {
		const result = new Map<SegmentId, Set<TileEntity>>();
		const buildingFrameNames: Map<
			FrameName,
			Set<number>
		> = this.tilerepo.getAllBuildingTileIndexes();
		for (const [frameName, tileIndexes] of buildingFrameNames) {
			if (
				tilerepoSettlementCastleBuildingsFrameNames.has(frameName) &&
				frameName !== FrameName.SETTLEMENT_CASTLE
			)
				continue;
			if (
				tilerepoPoiFrameNames.has(frameName) &&
				!tilerepoPOIs1FrameNames.has(frameName)
			)
				continue;

			for (const tileIndex of tileIndexes) {
				const segmentId = this.getSegmentIdByTileIndex(tileIndex);
				if (!segmentIds.has(segmentId)) continue;

				const entity: TileEntity | null =
					this.tilerepo.getTileEntityByTileIndex(tileIndex);
				if (!entity) continue;

				// skipneme neodhalene settlementy
				if ("isActive" in entity && !entity.isActive) continue;

				// skipneme budovy, ktore maju byt pripojene na settlement ale nie su
				if (
					entity.requirements.has(
						ResourceType.SETTLEMENT_CONNECTION,
					) &&
					entity.requirements.get(
						ResourceType.SETTLEMENT_CONNECTION,
					) === 1 &&
					!entity.isConnectedToMainSettlement
				)
					continue;

				// skipne nepreskumané POI
				if (
					entity instanceof AbstractPoiTileEntity &&
					!entity.isFullyExplored
				)
					continue;

				let entities: Set<TileEntity> | undefined =
					result.get(segmentId);
				if (!entities || !entities.size) {
					entities = new Set<TileEntity>();
					result.set(segmentId, entities);
				}
				entities.add(entity);
			}
		}

		return result;
	}

	/**
	 * Updates storage and production for all dirty segments.
	 */
	private updateDirtySegments(): void {
		const dirtySegmentIds = new Set<SegmentId>();
		for (const [id, data] of this.segments) {
			if (data.isDirty) dirtySegmentIds.add(id);
		}
		if (!dirtySegmentIds.size) {
			// Clean segment accumulators do not guarantee fresh cached entities.
			this.tilerepo.runEntityCacheGc(true);
			return;
		}

		this.tilerepo.destroyEntityCacheGc();
		this.tilerepo.runEntityCacheGc(true);
		try {
			const entitiesBySegment: Map<
				number,
				Set<TileEntity>
			> = this.getProductionEntitiesBySegmentIds(dirtySegmentIds);
			for (const segmentId of dirtySegmentIds) {
				const segmentData: SegmentValue = this.segments.get(segmentId)!;

				const entities: Set<TileEntity> =
					entitiesBySegment.get(segmentId) ?? new Set<TileEntity>();
				const clonedSegmentData: SegmentValue = clone(segmentData);

				const storageAccumulator: AccumulatorValue = {};
				const resourceAccumulator: AccumulatorValue = {};
				const incomeAccumulator: AccumulatorValue = {};
				const consumptionAccumulator: AccumulatorValue = {};

				// const settlementsWithIncomeQuest = this.settlementStore.getSideSettlementsWithIncomeQuest();

				for (const {
					storage,
					production,
					consumption,
					// assignedCastleTileEntity,
				} of entities) {
					if (storage.size) {
						for (const [resourceType, value] of storage) {
							if (resourceType === ResourceType.SETTLEMENT_CLICKS)
								continue;

							storageAccumulator[resourceType] =
								(storageAccumulator[resourceType] || 0) + value;
						}
					}

					if (production.size) {
						for (const [incomeResourceType, value] of production) {
							if (
								!RESOURCE_INCOME_REGEXP.test(incomeResourceType)
							)
								continue;

							incomeAccumulator[incomeResourceType] =
								(incomeAccumulator[incomeResourceType] || 0) +
								(value as number);

							const resourceType = incomeResourceType.replace(
								RESOURCE_INCOME_REGEXP,
								INCOME_DIFF_RESOURCE_TYPE.has(
									incomeResourceType,
								)
									? "Diff"
									: "",
							) as ResourceType;
							resourceAccumulator[resourceType] =
								(resourceAccumulator[resourceType] || 0) +
								(value as number);

							/*
							if (
								assignedCastleTileEntity &&
								settlementsWithIncomeQuest.has(
									`${assignedCastleTileEntity.x}|${assignedCastleTileEntity.y}`,
								)
							) {
								const { action, data } =
									assignedCastleTileEntity?.activeQuestObjective ||
									{};
								if (!action || !data) continue;

								if (
									action ===
									UpgradeObjectiveAction.SETTLEMENT_INCOME
								) {
									const resource =
										(data as UpgradeObjectiveResourceData)[
											"{resource}"
										] ?? null;
									if (!resource) continue;

									if (resource === incomeResourceType) {
										const settlement =
											this.settlementStore.getSettlementByCoordinates(
												{
													x: assignedCastleTileEntity.x,
													y: assignedCastleTileEntity.y,
												},
											);
										if (!settlement) continue;

										this.settlementStore.setSettlementPropsByCoordinates(
											{
												x: assignedCastleTileEntity.x,
												y: assignedCastleTileEntity.y,
											},
											{
												questData: {
													...settlement.questData,
													currentIncrement:
														value as number,
												},
											},
										);
									}
								}
							}
							*/
						}
					}

					if (consumption.size) {
						for (const [
							consumptionResourceType,
							value,
						] of consumption) {
							if (
								!RESOURCE_CONSUMPTION_REGEXP.test(
									consumptionResourceType,
								)
							)
								continue;

							consumptionAccumulator[consumptionResourceType] =
								(consumptionAccumulator[
									consumptionResourceType
								] || 0) + value;

							const resourceType =
								consumptionResourceType.replace(
									RESOURCE_CONSUMPTION_REGEXP,
									CONSUMPTION_DIFF_RESOURCE_TYPE.has(
										consumptionResourceType,
									)
										? "Diff"
										: "",
								) as ResourceType;
							resourceAccumulator[resourceType] =
								(resourceAccumulator[resourceType] || 0) -
								value;
						}
					}
				}

				Object.assign(clonedSegmentData, {
					isDirty: false,
					[AccumulatorName.STORAGE_ACCUMULATOR]: storageAccumulator,
					[AccumulatorName.RESOURCE_ACCUMULATOR]: resourceAccumulator,
					[AccumulatorName.INCOME_ACCUMULATOR]: incomeAccumulator,
					[AccumulatorName.CONSUMPTION_ACCUMULATOR]:
						consumptionAccumulator,
				});
				this.segments.set(segmentId, clonedSegmentData);
			}
		} finally {
			this.tilerepo.runEntityCacheGc(true);
			this.tilerepo.initEntityCacheGc();
		}
	}

	/**
	 * Returns all segment data combined.
	 */
	private get data(): Record<AccumulatorName, AccumulatorValue> {
		this.updateDirtySegments();

		const storageAccumulator: AccumulatorValue = {};
		const resourceAccumulator: AccumulatorValue = {};
		const incomeAccumulator: AccumulatorValue = {};
		const uncappedIncomeAccumulator: AccumulatorValue = {};
		const consumptionAccumulator: AccumulatorValue = {};

		for (const [, segmentData] of this.segments) {
			const storageAccumulatorEntries = Object.entries(
				segmentData.storageAccumulator,
			) as Array<[ResourceType, number]>;
			for (const [resourceType, value] of storageAccumulatorEntries) {
				if (!storageAccumulator[resourceType])
					storageAccumulator[resourceType] = 0;
				storageAccumulator[resourceType] += value;
			}

			const resourceAccumulatorEntries = Object.entries(
				segmentData.resourceAccumulator,
			) as Array<[ResourceType, number]>;
			for (const [resourceType, value] of resourceAccumulatorEntries) {
				if (!resourceAccumulator[resourceType])
					resourceAccumulator[resourceType] = 0;
				resourceAccumulator[resourceType] += value;
			}

			const incomeAccumulatorEntries = Object.entries(
				segmentData.incomeAccumulator,
			) as Array<[ResourceType, number]>;
			for (const [resourceType, value] of incomeAccumulatorEntries) {
				if (!incomeAccumulator[resourceType])
					incomeAccumulator[resourceType] = 0;
				incomeAccumulator[resourceType] += value;
			}

			const consumptionAccumulatorEntries = Object.entries(
				segmentData.consumptionAccumulator,
			) as Array<[ResourceType, number]>;
			for (const [resourceType, value] of consumptionAccumulatorEntries) {
				if (!consumptionAccumulator[resourceType])
					consumptionAccumulator[resourceType] = 0;
				consumptionAccumulator[resourceType] += value;
			}
		}

		// Apply active provision bonus after summing segment storage for subsequent limits & production
		const userStore = useUserStore();
		const activeProvisions = userStore.activeProvisions;
		const activeProvisionsCount = activeProvisions.length;

		if (activeProvisionsCount > 0) {
			const configBonus =
				royalProvisionRewards[
					RoyalProvisionRewardKey.InventoryStorageBoost
				]?.value ?? 1;

			const bonusPerPackage = configBonus - 1;
			const totalMultiplier = 1 + bonusPerPackage * activeProvisionsCount;

			for (const [resourceType, value] of Object.entries(
				storageAccumulator,
			) as Array<[ResourceType, number]>) {
				storageAccumulator[resourceType] = floorNumber(
					value * totalMultiplier,
				);
			}
		}

		this.FOOD_PENALTY_COEFFICIENT = SegmentManager.getPenaltyCoefficient(
			incomeAccumulator[ResourceType.FOOD_INCOME] || 0,
			consumptionAccumulator[ResourceType.FOOD_CONSUMPTION] || 0,
		);
		this.POPULATION_PENALTY_COEFFICIENT =
			SegmentManager.getPenaltyCoefficient(
				incomeAccumulator[ResourceType.POPULATION_INCOME] || 0,
				consumptionAccumulator[ResourceType.POPULATION_CONSUMPTION] ||
					0,
			);

		const resourceAccumulatorEntries = Object.entries(
			resourceAccumulator,
		) as Array<[ResourceType, number]>;
		for (const [resourceType, value] of resourceAccumulatorEntries) {
			if (resourceType.endsWith("Diff")) continue;
			if (value <= 0) continue;

			const isUncappedResource =
				UNCAPPED_RESOURCE_TYPES.has(resourceType);

			const currentValue = this.resourcesStore[resourceType] || 0;
			const limitValue = !isUncappedResource
				? storageAccumulator[resourceType] || 0
				: Number.MAX_SAFE_INTEGER;
			if (limitValue <= currentValue) {
				resourceAccumulator[resourceType] = 0;
				continue;
			}

			const valueWithPenalty = ceilNumber(
				value *
					this.FOOD_PENALTY_COEFFICIENT *
					this.POPULATION_PENALTY_COEFFICIENT,
			);
			const cappedValueWithPenalty =
				!isUncappedResource &&
				currentValue + valueWithPenalty > limitValue
					? limitValue - currentValue
					: valueWithPenalty;
			resourceAccumulator[resourceType] = cappedValueWithPenalty;
		}

		const incomeAccumulatorEntries = Object.entries(
			incomeAccumulator,
		) as Array<[ResourceType, number]>;
		for (const [incomeResourceType, value] of incomeAccumulatorEntries) {
			if (INCOME_DIFF_RESOURCE_TYPE.has(incomeResourceType)) continue;
			if (value <= 0) continue;

			const resourceType = incomeResourceType.replace(
				RESOURCE_INCOME_REGEXP,
				"",
			) as ResourceType;
			const isUncappedResource =
				UNCAPPED_RESOURCE_TYPES.has(resourceType);

			const valueWithPenalty = ceilNumber(
				value *
					this.FOOD_PENALTY_COEFFICIENT *
					this.POPULATION_PENALTY_COEFFICIENT,
			);

			const uncappedIncomeResourceType = incomeResourceType.replace(
				RESOURCE_INCOME_REGEXP,
				"UncappedIncome",
			) as ResourceType;
			if (uncappedIncomeResourceType in this.resourcesStore) {
				// Settlement upgrade objectives use *UncappedIncome and should not be affected
				// by temporary income boost. Keep penalties (food/pop) but remove boost.
				uncappedIncomeAccumulator[uncappedIncomeResourceType] =
					valueWithPenalty;
			}

			const uncappedIncomeWithoutPenaltiesResourceType =
				`${uncappedIncomeResourceType}WithoutPenalties` as ResourceType;
			if (
				uncappedIncomeWithoutPenaltiesResourceType in
				this.resourcesStore
			) {
				uncappedIncomeAccumulator[
					uncappedIncomeWithoutPenaltiesResourceType
				] =
					(uncappedIncomeAccumulator[
						uncappedIncomeWithoutPenaltiesResourceType
					] || 0) +
					// Tu nechceme boost ani penalties.
					ceilNumber(value);
			}

			const currentValue = this.resourcesStore[resourceType] || 0;
			const limitValue = !isUncappedResource
				? storageAccumulator[resourceType] || 0
				: Number.MAX_SAFE_INTEGER;
			if (limitValue <= currentValue) {
				incomeAccumulator[incomeResourceType] = 0;
				continue;
			}

			const cappedValueWithPenalty =
				!isUncappedResource &&
				currentValue + valueWithPenalty > limitValue
					? limitValue - currentValue
					: valueWithPenalty;
			incomeAccumulator[incomeResourceType] = cappedValueWithPenalty;
		}

		const questRewardEntries = Object.entries(
			this.questStore.rewardsByTick,
		) as Array<[ResourceType, number]>;
		for (const [rewardEnumKey, rewardValue] of questRewardEntries) {
			if (rewardEnumKey !== ResourceType.COINS)
				throw new GameError(
					GameErrorType.RESOURCE_ERROR,
					`Quest rewards must be coins, got ${rewardEnumKey}`,
				);

			incomeAccumulator[ResourceType.COINS_INCOME] =
				(incomeAccumulator[ResourceType.COINS_INCOME] || 0) +
				rewardValue;
			uncappedIncomeAccumulator[ResourceType.COINS_UNCAPPED_INCOME] =
				(uncappedIncomeAccumulator[
					ResourceType.COINS_UNCAPPED_INCOME
				] || 0) + rewardValue;
			resourceAccumulator[ResourceType.COINS] =
				(resourceAccumulator[ResourceType.COINS] || 0) + rewardValue;
		}

		// pridaj 1 coin za kazde odhalene policko, odpocitaj vodu, ta ma zisk podla upgradu naval_trade
		const {
			exploredTileCount,
			exploredWaterTileCount,
			exploredWaterTileIncome,
		} = this.tilerepo;
		const totalTileIncome =
			exploredTileCount -
			exploredWaterTileCount +
			exploredWaterTileIncome;
		incomeAccumulator[ResourceType.COINS_INCOME] =
			(incomeAccumulator[ResourceType.COINS_INCOME] || 0) +
			totalTileIncome;
		uncappedIncomeAccumulator[ResourceType.COINS_UNCAPPED_INCOME] =
			(uncappedIncomeAccumulator[ResourceType.COINS_UNCAPPED_INCOME] ||
				0) + totalTileIncome;
		resourceAccumulator[ResourceType.COINS] =
			(resourceAccumulator[ResourceType.COINS] || 0) + totalTileIncome;

		// Pre resource questy
		const questStore = useQuestStore();
		if (questStore.activeQuests.length > 0) {
			questStore.activeQuests.forEach((quest: QuestActive): void => {
				if (
					quest.objective.action ===
					QuestObjectiveActions.HAVE_RESOURCE
				) {
					const resourceType = quest.objective.type as ResourceType;
					quest = {
						...quest,
						progress: resourceAccumulator?.[resourceType] ?? 0,
					};
				}

				if (
					quest.objective.action ===
					QuestObjectiveActions.HAVE_STORAGE
				) {
					const resourceType = quest.objective.type as ResourceType;
					quest = {
						...quest,
						progress: storageAccumulator?.[resourceType] ?? 0,
					};
				}
			});
		}

		incomeAccumulator[ResourceType.EVENTIUM_INCOME] =
			(incomeAccumulator[ResourceType.EVENTIUM_INCOME] || 0) +
			eventRepository.getPassiveEventium();
		uncappedIncomeAccumulator[ResourceType.EVENTIUM_UNCAPPED_INCOME] =
			(uncappedIncomeAccumulator[ResourceType.EVENTIUM_UNCAPPED_INCOME] ||
				0) + eventRepository.getPassiveEventium();
		resourceAccumulator[ResourceType.EVENTIUM] =
			(resourceAccumulator[ResourceType.EVENTIUM] || 0) +
			eventRepository.getPassiveEventium();
		// Add zero values
		for (const resource of [
			ResourceType.FOOD_DIFF,
			ResourceType.POPULATION_DIFF,
			ResourceType.COINS,
			ResourceType.GEMS,
			ResourceType.LUMBER,
			ResourceType.STONE,
			ResourceType.CLAY,
			ResourceType.IRON,
			ResourceType.GOODS,
			ResourceType.STEEL,
			ResourceType.ARMY,
			ResourceType.RATIONS,
			ResourceType.EVENTIUM,
		]) {
			if (!(resource in storageAccumulator))
				storageAccumulator[resource] = 0;
			if (!(resource in resourceAccumulator))
				resourceAccumulator[resource] = 0;
			if (!(resource.replace("Diff", "") + "Income" in incomeAccumulator))
				incomeAccumulator[
					`${resource.replace("Diff", "")}Income` as ResourceType
				] = 0;
			if (
				!(
					resource.replace("Diff", "") + "UncappedIncome" in
					uncappedIncomeAccumulator
				)
			)
				uncappedIncomeAccumulator[
					`${resource.replace("Diff", "")}UncappedIncome` as ResourceType
				] = 0;
			if (
				!(
					resource.replace("Diff", "") + "Consumption" in
					consumptionAccumulator
				)
			)
				consumptionAccumulator[
					`${resource.replace("Diff", "")}Consumption` as ResourceType
				] = 0;
			const uncappedIncomeWithoutPenaltiesResourceType =
				`${resource.replace("Diff", "")}UncappedIncomeWithoutPenalties` as ResourceType;
			if (
				!(
					uncappedIncomeWithoutPenaltiesResourceType in
					uncappedIncomeAccumulator
				)
			)
				uncappedIncomeAccumulator[
					uncappedIncomeWithoutPenaltiesResourceType
				] = 0;
		}

		return {
			[AccumulatorName.STORAGE_ACCUMULATOR]: storageAccumulator,
			[AccumulatorName.RESOURCE_ACCUMULATOR]: resourceAccumulator,
			[AccumulatorName.INCOME_ACCUMULATOR]: incomeAccumulator,
			[AccumulatorName.UNCAPPED_INCOME_ACCUMULATOR]:
				uncappedIncomeAccumulator,
			[AccumulatorName.CONSUMPTION_ACCUMULATOR]: consumptionAccumulator,
		};
	}

	/**
	 * Runs the segment manager, updating stores based on accumulators.
	 */
	public run(simulate?: boolean, isTick = false): AccumulatorValue {
		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.time("SegmentManager.run()");
		}

		const { data } = this;

		const accumulatorTypes = [
			AccumulatorName.STORAGE_ACCUMULATOR,
			AccumulatorName.RESOURCE_ACCUMULATOR,
			AccumulatorName.INCOME_ACCUMULATOR,
			AccumulatorName.UNCAPPED_INCOME_ACCUMULATOR,
			AccumulatorName.CONSUMPTION_ACCUMULATOR,
		];
		for (const accumulatorType of accumulatorTypes) {
			const entries = Object.entries(data[accumulatorType]) as Array<
				[ResourceType, number]
			>;
			for (const [resourceType, value] of entries) {
				if (accumulatorType !== AccumulatorName.RESOURCE_ACCUMULATOR) {
					if (accumulatorType === AccumulatorName.STORAGE_ACCUMULATOR)
						this.resourcesStorageStore.set(resourceType, value);
					else this.resourcesStore.set(resourceType, value, false);
					continue;
				}

				const isDiffResource = resourceType.endsWith("Diff");
				if (simulate && !isDiffResource) continue;

				const action = isDiffResource
					? "set"
					: value > 0
						? "increase"
						: "decrease";
				if (action !== "set" && value === 0) continue;

				let totalValue = value;
				if (
					![
						ResourceType.FOOD_DIFF,
						ResourceType.POPULATION_DIFF,
					].includes(resourceType) &&
					isTick &&
					this.runtimeStore.incomeBoostActive
				) {
					totalValue = value * INCOME_BOOST_COEFFICIENT;
				}
				this.resourcesStore[action](
					resourceType,
					action !== "set" ? Math.abs(totalValue) : totalValue,
					`SegmentManager: ${action} ${accumulatorType}`,
					// @ts-expect-error - only decrease method has 4th param
					action === "decrease" ? true : undefined,
				);
			}
		}

		this.resourcesStore.set(
			ResourceType.FISH_INCOME,
			this.tilerepo.getFishIncome(),
			false,
		);

		// if (!simulate) this.runSettlementQuestHandler();
		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.timeEnd("SegmentManager.run()");
		}
		// console.log(`SegmentManager.run(${simulate}) data:`, data);

		return data.resourceAccumulator;
	}

	/*
	public runSettlementQuestHandler(multiplier: number = 1): void {
		const settlementsWithIncomeQuest =
			this.settlementStore.getSideSettlementsWithIncomeQuest();

		for (const key of settlementsWithIncomeQuest) {
			// eslint-disable-next-line no-restricted-syntax
			const [x, y] = key.split("|").map(Number);
			const settlement = this.settlementStore.getSettlementByCoordinates({
				x,
				y,
			});
			if (!settlement) continue;

			this.settlementStore.setSettlementPropsByCoordinates(
				{
					x,
					y,
				},
				{
					questData: {
						...settlement.questData,
						current:
							(settlement.questData?.current ?? 0) +
							ceilNumber(
								(settlement.questData?.currentIncrement ?? 0) *
									this.FOOD_PENALTY_COEFFICIENT *
									this.POPULATION_PENALTY_COEFFICIENT *
									multiplier,
							),
					},
				},
			);
		}
	}
	*/
}

export default SegmentManager;
